import React, { useMemo, useState } from 'react'
import { Typography } from 'antd'
import { Todo, TimeSlot } from '../types'
import { getDayName, calculateTodoHeight, calculateTodoTop, timeToMinutes, PIXELS_PER_15_MIN } from '../utils/timeUtils'
import TodoItem from './TodoItem'
import GridCell from './GridCell'
import DropTarget from './DropTarget'
import { Settings } from '../types'

const DayColumn: React.FC<{
  dayOfWeek: number
  dayDate: Date
  timeSlots: TimeSlot[]
  todos: Todo[]
  onCellClick: (dayOfWeek: number, time: string, element: HTMLElement) => void
  onEditTodo: (todo: Todo, element: HTMLElement) => void
  onDeleteTodo: (id: string) => void
  onMoveTodo: (todoId: string, newDayOfWeek: number, newStartTime: string) => void
  onResizeTodo: (id: string, newHeight: number) => void
  onToggleComplete: (id: string, completed: boolean) => void
  settings: Settings
  isResizing?: boolean
  onResizingChange?: (resizing: boolean) => void
  mutatingMap: Record<string, boolean>
}> = ({
  dayOfWeek,
  dayDate,
  timeSlots,
  todos,
  onCellClick,
  onEditTodo,
  onDeleteTodo,
  onMoveTodo,
  onResizeTodo,
  onToggleComplete,
  settings,
  isResizing,
  onResizingChange,
  mutatingMap
}) => {
  // Calculate overlapping todos and their positions
  const processedTodos = useMemo(() => {
    const sortedTodos = [...todos].sort((a, b) => {
      const startA = timeToMinutes(a.start_time)
      const startB = timeToMinutes(b.start_time)
      const endA = timeToMinutes(a.end_time)
      const endB = timeToMinutes(b.end_time)

      // Group todos by overlapping time periods
      if (startA !== startB) return startA - startB
      if (endA !== endB) return endA - endB
      return 0
    })

    // 保持100%宽度与左侧对齐，不进行重叠压缩
    return sortedTodos.map((todo) => ({
      ...todo,
      width: '100%',
      left: '0%'
    }))
  }, [todos])

  const [previewY, setPreviewY] = useState<number | null>(null)
  const [activeTodoId, setActiveTodoId] = useState<string | null>(null)

  return (
    <div className="day-column">
      <div
        className="day-grid"
        style={{ height: (timeSlots.length - 1) * PIXELS_PER_15_MIN }}
        onClick={() => setActiveTodoId(null)}
      >
        {/* Lunch break background (12:00-14:00) */}
        {(() => {
          const startMinutes = 12 * 60
          const endMinutes = 14 * 60
          const top = Math.max(0, (startMinutes - (7 * 60)) * (PIXELS_PER_15_MIN / 15))
          const height = (endMinutes - startMinutes) * (PIXELS_PER_15_MIN / 15)
          return (
            <div
              aria-hidden
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                top,
                height,
                background: settings?.lunch_color || 'rgba(82, 196, 26, 0.08)',
                zIndex: 0,
                pointerEvents: 'none'
              }}
            />
          )
        })()}
        {/* Grid cells for clicking (exclude the last boundary slot) */}
        {timeSlots.slice(0, -1).map((slot, index) => (
          <DropTarget
            key={slot.time}
            time={slot.time}
            dayOfWeek={dayOfWeek}
            onMoveTodo={onMoveTodo}
          >
            <GridCell
              dayOfWeek={dayOfWeek}
              time={slot.time}
              slot={slot}
              index={index}
              disabled={!!isResizing}
              onClick={(element) => onCellClick(dayOfWeek, slot.time, element)}
            />
          </DropTarget>
        ))}

        {/* Todo items */}
        {processedTodos.map(todo => (
          <TodoItem
            key={todo.id}
            todo={todo}
            top={calculateTodoTop(todo.start_time)}
            height={calculateTodoHeight(todo.start_time, todo.end_time)}
            width={todo.width}
            left={todo.left}
            onEdit={(todo, element) => onEditTodo(todo, element)}
            onDelete={onDeleteTodo}
            onResize={onResizeTodo}
            onToggleComplete={onToggleComplete}
            settings={settings}
            onResizingChange={onResizingChange}
            onPreviewChange={(y) => setPreviewY(y ?? null)}
            isActive={activeTodoId === todo.id}
            onActivate={setActiveTodoId}
            isMutating={!!mutatingMap[todo.id]}
          />
        ))}

        {/* 拉伸预览蓝线（绝对定位到列内）*/}
        {typeof previewY === 'number' && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: Math.max(0, previewY - 2),
              height: 2,
              background: '#1890ff',
              pointerEvents: 'none',
              zIndex: 25
            }}
          />
        )}
      </div>
    </div>
  )
}

export default DayColumn